import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/tenant";
import { useRealtime } from "@/lib/realtime";
import { PageHeader, Panel, Empty } from "@/components/Bits";
import { toast } from "sonner";
import { Check, X, Inbox, Brain, PowerOff, Ban, ScanSearch, ShieldCheck, AlertTriangle } from "lucide-react";

const ICONS = { isolate: PowerOff, kill_process: Ban, scan: ScanSearch, secure: ShieldCheck, release: ShieldCheck };

export default function Approvals() {
  const { tenantId } = useTenant();
  const { refreshPending } = useRealtime();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const focus = params.get("focus");
  const [tab, setTab] = useState(focus ? "all" : "pending");

  const { data = [], isLoading } = useQuery({
    queryKey: ["approvals", tenantId, tab],
    queryFn: () => api.get(`/approvals/${tenantId}`, { params: tab === "all" ? {} : { state: tab } }).then((r) => r.data),
    enabled: !!tenantId,
  });

  const decide = useMutation({
    mutationFn: ({ id, decision }) => api.post(`/approvals/${id}/decide`, { decision }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(d.status === "approved" ? d.result : "Proposal rejected");
      qc.invalidateQueries({ queryKey: ["approvals", tenantId] });
      qc.invalidateQueries({ queryKey: ["endpoints", tenantId] });
      qc.invalidateQueries({ queryKey: ["incidents", tenantId] });
      refreshPending();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Decision failed"),
  });

  useEffect(() => {
    const decision = params.get("decide");
    if (focus && decision && data.some((a) => a.id === focus && a.status === "pending")) {
      decide.mutate({ id: focus, decision });
      setParams({ focus });
    }
    if (focus) document.getElementById(`approval-${focus}`)?.scrollIntoView({ block: "center" });
  }, [data, focus]); // eslint-disable-line

  if (!tenantId) return <Empty msg="Select an MSP tenant" />;

  return (
    <>
      <PageHeader title="Approval Inbox" kicker="Level 2 · Human-in-the-loop response">
        <div data-testid="approvals-tabs" className="flex border hairline">
          {["pending", "approved", "rejected", "all"].map((t) => (
            <button key={t} data-testid={`approvals-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest transition-colors duration-150 ${tab === t ? "bg-cyan-400 text-[#15151F]" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>{t}</button>
          ))}
        </div>
      </PageHeader>
      <div className="p-6">
        <Panel>
          <div className="divide-y hairline">
            {data.map((a) => <Row key={a.id} a={a} focused={a.id === focus} busy={decide.isPending} onDecide={(decision) => decide.mutate({ id: a.id, decision })} />)}
            {!isLoading && data.length === 0 && (
              <div className="p-12 text-center">
                <Inbox size={28} className="mx-auto text-zinc-600 mb-3" />
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">No {tab === "all" ? "" : tab} proposals</div>
                <div className="text-xs text-zinc-600 mt-1">The AI proposes actions here when L2 is on and L3 autonomy is off, or when offensive tooling is seen by the live agent. Proposals left 15+ minutes auto-escalate to critical.</div>
              </div>
            )}
          </div>
        </Panel>
      </div>
    </>
  );
}

const Row = ({ a, onDecide, busy, focused }) => {
  const Icon = ICONS[a.action] || Brain;
  const pending = a.status === "pending";
  const ageMin = Math.max(0, Math.round((Date.now() - new Date(a.ts).getTime()) / 60000));
  return (
    <div id={`approval-${a.id}`} data-testid={`approval-${a.id}`} className={`px-4 py-3 flex items-start gap-4 ${a.escalated && pending ? "bg-red-950/20 border-l-2 border-red-500" : ""} ${focused ? "ring-1 ring-inset ring-cyan-400/60" : ""}`}>
      <div className={`w-8 h-8 border flex items-center justify-center shrink-0 ${pending ? (a.escalated ? "border-red-500/60 text-red-400 bg-red-500/5" : "border-cyan-400/50 text-cyan-300 bg-cyan-400/5") : "border-white/10 text-zinc-500"}`}><Icon size={14} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">{a.action.replace("_", " ")}</span>
          <span className="font-semibold text-sm">{a.hostname}</span>
          <span className="text-xs text-zinc-500">{a.user}</span>
          {a.confidence && <span className="font-mono text-[10px] text-orange-400 border border-orange-400/40 px-1">{Math.round(a.confidence * 100)}%</span>}
          {a.escalated && <span data-testid={`escalated-${a.id}`} className="font-mono text-[10px] uppercase tracking-widest text-red-400 border border-red-500/50 bg-red-500/10 px-1.5 flex items-center gap-1"><AlertTriangle size={10} /> Critical · SLA breached</span>}
          {pending && !a.escalated && <span className="font-mono text-[10px] text-zinc-500">{ageMin}m waiting · escalates at 15m</span>}
        </div>
        <div className="text-xs text-zinc-400 mt-0.5">{a.reason}</div>
        <div className="font-mono text-[10px] text-zinc-600 mt-1">
          proposed by {a.proposed_by} · {new Date(a.ts).toLocaleString()}
          {!pending && <> · <span className={a.status === "approved" ? "text-emerald-400" : "text-zinc-400"}>{a.status}</span> by {a.decided_by} {a.result ? `· ${a.result}` : ""}</>}
        </div>
      </div>
      {pending && (
        <div className="flex gap-2 shrink-0">
          <button data-testid={`approve-${a.id}`} disabled={busy} onClick={() => onDecide("approve")}
            className="border border-emerald-400/60 text-emerald-300 hover:bg-emerald-400/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 disabled:opacity-50 transition-colors duration-150"><Check size={12} /> Approve</button>
          <button data-testid={`reject-${a.id}`} disabled={busy} onClick={() => onDecide("reject")}
            className="border border-white/15 text-zinc-400 hover:bg-white/5 hover:text-white px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 disabled:opacity-50 transition-colors duration-150"><X size={12} /> Reject</button>
        </div>
      )}
    </div>
  );
};
